import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../redux/store';
import { selectRelevantDocumentMetadata } from '../../redux/selectors/aiLegalMessageSelectors';
import { documentSelectOneObjectByQuery, knowledgebaseSelectOneObjectByQuery } from '../../redux/thunks/DBServiceThunks';
import RelevantDocumentCard from '../common/RelevantDocumentCard';


interface RelevantDocumentItemProps {
  documentId: string;
  messageId: string; // Required to get the correct message
}

/**
 * Container component for RelevantDocumentItem
 * Fetches document data from Redux and passes it to DocumentCard presentation component
 */
const RelevantDocumentItem: React.FC<RelevantDocumentItemProps> = ({
  documentId,
  messageId
}) => {
  const { t } = useTranslation();
  // Get document metadata using custom selector
  const documentMetadata = useAppSelector((state) => selectRelevantDocumentMetadata(state, messageId, documentId));

  // Extract metadata with fallbacks
  const relevance = documentMetadata?.relevance || 'not_relevant';
  const contentType = documentMetadata?.contentType || 'other';
  const reasoning = (documentMetadata as any)?.reasoning;
  const confidence = (documentMetadata as any)?.confidence;
  const keyTerms = (documentMetadata as any)?.keyTerms;

  // Get document from Redux state using proper selector
  const document = useAppSelector(state =>
    documentSelectOneObjectByQuery(state.DBDocumentReducer, { _id: documentId })
  );

  // Get knowledgebase from Redux state for legal domain
  const knowledgebase = useAppSelector(state =>
    document?.knowledgebaseId
      ? knowledgebaseSelectOneObjectByQuery(state.DBKnowledgebaseReducer, { _id: document.knowledgebaseId })
      : null
  );

  // Determine if this is a user-uploaded document
  const isUserUpload = !!knowledgebase?.caseId;

  // Build props for DocumentCard
  const documentTitle = document?.title || document?.heading || document?.name || t('common.fallbacks.untitledDocument', 'Untitled Document');

  // Ensure subtitle is different from title
  const documentSubtitle = isUserUpload
    ? undefined
    : document?.metadata?.source && document.metadata.source !== documentTitle
      ? document.metadata.source
      : undefined;

  const knowledgebaseName = knowledgebase?.name || '';
  const jurisdiction = knowledgebase?.jurisdiction || '';
  const documentUrl = document?.metadata?.document_url;

  return (
    <RelevantDocumentCard
      documentId={documentId}
      documentTitle={documentTitle}
      documentSubtitle={documentSubtitle}
      knowledgebaseName={knowledgebaseName}
      jurisdiction={jurisdiction}
      relevance={relevance}
      contentType={contentType}
      score={0} // Score not available in this context
      confidence={confidence}
      reasoning={reasoning}
      keyTerms={keyTerms}
      showScore={false}
      isUserUpload={isUserUpload}
      documentUrl={documentUrl}
    />
  );
};

export default RelevantDocumentItem;
